/**
 * マッチング API hook
 * フリーマッチング・メール詳細画面からマッチング実行 & 結果ステータス更新を行う
 */
import { useCallback, useState } from "react";
import { restClient } from "../lib/rest-client";

// =====================================
// Types
// =====================================

export interface MatchingResultItem {
  id: string;
  sourceEmailId: string;
  sourceType: string;
  matchedEmailId: string | null;
  matchedSubject: string;
  totalScore: number;
  skillScore: number | null;
  rewardScore: number | null;
  locationScore: number | null;
  availabilityScore: number | null;
  aiReasoning: string | null;
  aiHighlights: string[] | null;
  aiRisks: string[] | null;
  status: string;
  actedBy: string | null;
  actedAt: string | null;
  createdAt: string;
}

export interface MatchingJobResult {
  id: string;
  sourceEmailId: string;
  sourceType: string;
  sourceSubject: string;
  triggerType: string;
  status: string;
  candidatesSearched: number;
  candidatesScored: number;
  resultsSaved: number;
  results: MatchingResultItem[];
  createdAt: string;
  updatedAt: string;
}

interface MatchingJobListResponse {
  items: MatchingJobResult[];
  total: number;
  page: number;
  limit: number;
}

export interface UseFreeMatchingApiResult {
  executeMatching: (
    sourceEmailId: string,
    sourceType: string,
  ) => Promise<MatchingJobResult>;
  executeFreetextMatching: (
    candidateInfo: string,
  ) => Promise<MatchingJobResult>;
  fetchExistingResults: (sourceEmailId: string) => Promise<void>;
  updateResultStatus: (
    resultId: string,
    status: string,
  ) => Promise<MatchingResultItem>;
  clearResult: () => void;
  loading: boolean;
  initialLoading: boolean;
  error: Error | undefined;
  jobResult: MatchingJobResult | undefined;
}

/**
 * マッチング API を実行する hook
 * @return {UseFreeMatchingApiResult} マッチング実行関数、ステータス更新関数、状態
 */
export function useFreeMatchingApi(): UseFreeMatchingApiResult {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [jobResult, setJobResult] = useState<MatchingJobResult | undefined>(
    undefined,
  );

  const executeMatching = useCallback(
    async (
      sourceEmailId: string,
      sourceType: string,
    ): Promise<MatchingJobResult> => {
      setLoading(true);
      setError(undefined);

      try {
        const result = await restClient.post<MatchingJobResult>(
          "/matching/jobs",
          { sourceEmailId, sourceType },
        );
        setJobResult(result);
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const executeFreetextMatching = useCallback(
    async (candidateInfo: string): Promise<MatchingJobResult> => {
      setLoading(true);
      setError(undefined);

      try {
        const result = await restClient.post<MatchingJobResult>(
          "/matching/jobs",
          { candidateInfo },
        );
        setJobResult(result);
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const fetchExistingResults = useCallback(
    async (sourceEmailId: string): Promise<void> => {
      setInitialLoading(true);
      try {
        const response = await restClient.get<MatchingJobListResponse>(
          `/matching/jobs?source_email_id=${encodeURIComponent(sourceEmailId)}&limit=1`,
        );
        if (response.items.length > 0) {
          setJobResult(response.items[0]);
        }
      } catch {
        // 既存結果の取得失敗は致命的ではないので静かに無視
      } finally {
        setInitialLoading(false);
      }
    },
    [],
  );

  const clearResult = useCallback(() => {
    setJobResult(undefined);
    setError(undefined);
  }, []);

  const updateResultStatus = useCallback(
    async (resultId: string, status: string): Promise<MatchingResultItem> => {
      try {
        const updated = await restClient.patch<MatchingResultItem>(
          `/matching/results/${resultId}/status`,
          { status },
        );

        // jobResult 内の該当 result を更新
        setJobResult(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            results: prev.results.map(r => (r.id === resultId ? updated : r)),
          };
        });

        return updated;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        throw err;
      }
    },
    [],
  );

  return {
    executeMatching,
    executeFreetextMatching,
    fetchExistingResults,
    updateResultStatus,
    clearResult,
    loading,
    initialLoading,
    error,
    jobResult,
  };
}

export default useFreeMatchingApi;
